import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResponse, HttpStatusCode } from 'axios';

import { useApi } from '../../../../context/ApiProvider';
import {
  TenantFinanceSummaryInterface,
  TenantMonthStakeStatsInterface,
  TenantPaymentSimpleInterface
} from '../../../../shared/type/finance.type';
import formatDate from '../../../../utils/formatDate';

export const useTenantFinances = () => {
  const today = new Date();
  const { id: tenantId } = useParams();
  const { apiTenantFinancialHistoryController, apiReportController } = useApi();

  const [financeSummary, setFinanceSummary] = useState<TenantFinanceSummaryInterface>();
  const [stakeStats, setStakeStats] = useState<TenantMonthStakeStatsInterface[]>([]);
  const [tenantPayments, setTenantPayments] = useState<TenantPaymentSimpleInterface[]>([]);
  const [dateForSummaryStats, setDateForSummaryStats] = useState({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate()
  });
  const [dateForMonthStakeStats, setDateForMonthStakeStats] = useState({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate()
  });
  const todayDateLabel = formatDate(today, 'dd-mmm-yyyy');

  const fetchTenantFinanceSummary = useCallback(async () => {
    try {
      const response: AxiosResponse<TenantFinanceSummaryInterface> =
        await apiTenantFinancialHistoryController('tenant-finance-summary').get('', {
          params: {
            tenantId,
            year: dateForSummaryStats.year,
            month: dateForSummaryStats.month
          }
        });

      if (response.status !== HttpStatusCode.Ok) return;

      setFinanceSummary(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [tenantId, dateForSummaryStats]);

  const fetchTenantMonthStakeStatistics = useCallback(async () => {
    try {
      const response: AxiosResponse<TenantMonthStakeStatsInterface[]> =
        await apiTenantFinancialHistoryController('month-stake-statistics').get('', {
          params: {
            tenantId,
            year: dateForMonthStakeStats.year,
            month: dateForMonthStakeStats.month
          }
        });

      if (response.status !== HttpStatusCode.Ok) return;
      setStakeStats(response.data);

    } catch (error) {
      console.error(error);
    }
  }, [tenantId, dateForMonthStakeStats]);

  const fetchTenantPayments = useCallback(async () => {
    try {
      console.info(dateForMonthStakeStats);
      const response: AxiosResponse<TenantPaymentSimpleInterface[]> =
        await apiTenantFinancialHistoryController('tenant-payments-for-month').get('', {
          params: {
            tenantId,
            year: dateForMonthStakeStats.year,
            month: dateForMonthStakeStats.month
          }
        });

      if (response.status !== HttpStatusCode.Ok) return;
      setTenantPayments(response.data);

    } catch (error) {
      console.error(error);
    }
  }, [tenantId, dateForMonthStakeStats]);

  const onDownloadReport = async (outputType: 'PDF' | 'XLSX') => {
    try {
      const response = await apiReportController('month-stake-statistics').get('', {
        params: {
          tenantId,
          year: dateForSummaryStats.year,
          month: dateForSummaryStats.month,
          outputType
        },
        responseType: 'blob'
      });

      const fileName = response.headers['content-disposition']?.split('filename=')[1];

      const blob = new Blob([response.data], {
        type: outputType === 'PDF' ? 'application/pdf' : 'application/vnd.ms-excel'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  };

  const onDownloadPaymentReport = async (paymentId:number, outputType: 'PDF' | 'XLSX') => {
    try {
      const response = await apiReportController('payments-to-tenant').get('', {
        params: {
          tenantPaymentId: paymentId,
          year: dateForSummaryStats.year,
          month: dateForSummaryStats.month,
          outputType
        },
        responseType: 'blob'
      });

      const fileName = response.headers['content-disposition']?.split('filename=')[1];

      const blob = new Blob([response.data], {
        type: outputType === 'PDF' ? 'application/pdf' : 'application/vnd.ms-excel'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    fetchTenantFinanceSummary();
  }, [tenantId, dateForSummaryStats]);

  useEffect(() => {
    if (!tenantId) return;
    fetchTenantMonthStakeStatistics();
  }, [tenantId, dateForMonthStakeStats]);

  useEffect(() => {
    if (!tenantId) return;
    fetchTenantPayments();
  }, [tenantId, dateForMonthStakeStats]);

  return {
    financeSummary,
    stakeStats,
    tenantPayments,
    dateForSummaryStats,
    setDateForSummaryStats,
    dateForMonthStakeStats,
    setDateForMonthStakeStats,
    onDownloadReport,
    onDownloadPaymentReport,
    todayDateLabel
  };
};
